import { appRoutePath, appRoutePatterns } from './appRoutePatterns';
import {
  canonicalInternalPath,
  containsControlCharacter,
  INTERNAL_APP_ORIGIN,
  normalizeInternalAppReturnPath,
} from '@/types/internalNavigation';

export const OPERATIONS_PLAN_DRAFT_STAGE = 'plan' as const;
export const OPERATIONS_PLAN_DRAFT_LEDGER_VIEW = 'stands' as const;

const CONTEXT_KEYS = new Set([
  'ops_origin',
  'ops_property',
  'ops_plan_id',
  'ops_plan_revision',
  'ops_draft_revision',
  'ops_revaluation_revision',
  'ops_run_id',
  'ops_branch_id',
  'ops_stage',
  'ops_ledger_view',
  'ops_stand',
  'ops_return_anchor',
  'ops_context_ref',
]);

const RETURN_CONTEXT_KEYS = new Set([
  'plan_id',
  'stage',
  'ledger_view',
  'stand',
  'return_anchor',
  'return_to',
]);

export interface OperationsPlanDraftHandoffContext {
  readonly originRoute: string;
  readonly propertyId: string;
  readonly planId: string;
  readonly planRevision: string;
  readonly draftRevision: string;
  readonly revaluationRevision: string;
  readonly runId: string;
  readonly branchId: string;
  readonly stage: typeof OPERATIONS_PLAN_DRAFT_STAGE;
  readonly ledgerView: typeof OPERATIONS_PLAN_DRAFT_LEDGER_VIEW;
  readonly standId: string;
  readonly returnAnchor: string;
  readonly returnTo: string;
  readonly contextRef: string;
}

export interface OperationsPlanDraftHandoffInput {
  readonly propertyId: string;
  readonly planId: string;
  readonly planRevision: string;
  readonly draftRevision: string;
  readonly revaluationRevision: string;
  readonly runId: string;
  readonly branchId: string;
  readonly standId: string;
  readonly ownerReturnTo?: string | null;
}

export interface OperationsPlanDraftHandoffExpectation {
  readonly propertyId?: string | null;
  readonly standId?: string | null;
}

export interface OperationsPlanDraftReturnContext {
  readonly planId: string;
  readonly stage: typeof OPERATIONS_PLAN_DRAFT_STAGE;
  readonly ledgerView: typeof OPERATIONS_PLAN_DRAFT_LEDGER_VIEW;
  readonly standId: string;
  readonly returnAnchor: string;
}

function requiredPart(value: string, label: string): string {
  const normalized = value.trim();
  if (!normalized || normalized.length > 512 || containsControlCharacter(normalized)) {
    throw new Error(`Operations plan draft handoff: ${label} is invalid.`);
  }
  return normalized;
}

function optionalPart(value: string | null): string | null {
  if (value === null) return null;
  try {
    return requiredPart(value, 'context value');
  } catch {
    return null;
  }
}

function uniquePart(params: URLSearchParams, key: string): string | null {
  const values = params.getAll(key);
  return values.length === 1 ? optionalPart(values[0] ?? null) : null;
}

function uniqueReturnPath(params: URLSearchParams): string | null {
  const values = params.getAll('return_to');
  // Return URLs carry nested owner context, not a single 512-character ID.
  // Use the shared URL boundary while still rejecting duplicate parameters.
  return values.length === 1 ? normalizeInternalAppReturnPath(values[0]) : null;
}

function operationsPlanPath(propertyId: string): string {
  return appRoutePath(appRoutePatterns.operationsPlanning, { propertyId });
}

export function operationsPlanStandRowAnchor(standId: string): string {
  return `stand:${requiredPart(standId, 'stand identity')}`;
}

function stableContextHash(parts: readonly string[]): string {
  let hash = 0x811c9dc5;
  const input = parts.join('\u001f');
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `ops-plan-draft.v1:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

function contextReference(
  context: Omit<OperationsPlanDraftHandoffContext, 'contextRef' | 'returnTo'>,
): string {
  return stableContextHash([
    context.originRoute,
    context.propertyId,
    context.planId,
    context.planRevision,
    context.draftRevision,
    context.revaluationRevision,
    context.runId,
    context.branchId,
    context.stage,
    context.ledgerView,
    context.standId,
    context.returnAnchor,
  ]);
}

function buildReturnTo(
  propertyId: string,
  planId: string,
  standId: string,
  returnAnchor: string,
  ownerReturnTo: string | null = null,
): string {
  const target = new URL(operationsPlanPath(propertyId), INTERNAL_APP_ORIGIN);
  target.searchParams.set('plan_id', planId);
  target.searchParams.set('stage', OPERATIONS_PLAN_DRAFT_STAGE);
  target.searchParams.set('ledger_view', OPERATIONS_PLAN_DRAFT_LEDGER_VIEW);
  target.searchParams.set('stand', standId);
  target.searchParams.set('return_anchor', returnAnchor);
  const safeOwnerReturn = normalizeInternalAppReturnPath(ownerReturnTo);
  if (safeOwnerReturn) target.searchParams.set('return_to', safeOwnerReturn);
  return canonicalInternalPath(target);
}

export function parseOperationsPlanDraftReturn(
  search: string,
  _propertyId: string,
): OperationsPlanDraftReturnContext | null {
  const params = new URLSearchParams(search);
  if ([...params.keys()].some((key) => !RETURN_CONTEXT_KEYS.has(key))) return null;
  const planId = uniquePart(params, 'plan_id');
  const stage = uniquePart(params, 'stage');
  const ledgerView = uniquePart(params, 'ledger_view');
  const standId = uniquePart(params, 'stand');
  const returnAnchor = uniquePart(params, 'return_anchor');
  const ownerReturnTo = uniqueReturnPath(params);
  if (params.has('return_to') && ownerReturnTo === null) return null;
  const present = [planId, stage, ledgerView, standId, returnAnchor].some(
    (value) => value !== null,
  );
  if (!present) return null;
  if (
    !planId ||
    stage !== OPERATIONS_PLAN_DRAFT_STAGE ||
    ledgerView !== OPERATIONS_PLAN_DRAFT_LEDGER_VIEW ||
    !standId ||
    returnAnchor !== operationsPlanStandRowAnchor(standId)
  ) {
    return null;
  }
  return { planId, stage, ledgerView, standId, returnAnchor };
}

function validReturnTo(
  returnTo: string,
  propertyId: string,
  planId: string,
  standId: string,
  returnAnchor: string,
): string | null {
  const safe = normalizeInternalAppReturnPath(returnTo);
  if (!safe) return null;
  const candidate = new URL(safe, INTERNAL_APP_ORIGIN);
  const ownerReturnTo = uniqueReturnPath(candidate.searchParams);
  return safe === buildReturnTo(propertyId, planId, standId, returnAnchor, ownerReturnTo)
    ? safe
    : null;
}

export function hasOperationsPlanDraftHandoff(search: string): boolean {
  const params = new URLSearchParams(search);
  return [...params.keys()].some((key) => key.startsWith('ops_'));
}

/** Build one complete, immutable U5 -> Stand Detail draft-editing handoff. */
export function buildOperationsPlanDraftHandoff(input: OperationsPlanDraftHandoffInput): string {
  const propertyId = requiredPart(input.propertyId, 'property identity');
  const standId = requiredPart(input.standId, 'stand identity');
  const originRoute = operationsPlanPath(propertyId);
  const returnAnchor = operationsPlanStandRowAnchor(standId);
  const contextWithoutReturn = {
    originRoute,
    propertyId,
    planId: requiredPart(input.planId, 'plan identity'),
    planRevision: requiredPart(input.planRevision, 'plan revision'),
    draftRevision: requiredPart(input.draftRevision, 'draft revision'),
    revaluationRevision: requiredPart(input.revaluationRevision, 'revaluation revision'),
    runId: requiredPart(input.runId, 'simulation run identity'),
    branchId: requiredPart(input.branchId, 'simulation branch identity'),
    stage: OPERATIONS_PLAN_DRAFT_STAGE,
    ledgerView: OPERATIONS_PLAN_DRAFT_LEDGER_VIEW,
    standId,
    returnAnchor,
  } as const;
  const contextRef = contextReference(contextWithoutReturn);
  const returnTo = buildReturnTo(
    propertyId,
    contextWithoutReturn.planId,
    standId,
    returnAnchor,
    input.ownerReturnTo ?? null,
  );
  const target = new URL(
    appRoutePath(appRoutePatterns.standDetail, { propertyId, standId }),
    INTERNAL_APP_ORIGIN,
  );
  target.searchParams.set('ops_origin', originRoute);
  target.searchParams.set('ops_property', propertyId);
  target.searchParams.set('ops_plan_id', contextWithoutReturn.planId);
  target.searchParams.set('ops_plan_revision', contextWithoutReturn.planRevision);
  target.searchParams.set('ops_draft_revision', contextWithoutReturn.draftRevision);
  target.searchParams.set('ops_revaluation_revision', contextWithoutReturn.revaluationRevision);
  target.searchParams.set('ops_run_id', contextWithoutReturn.runId);
  target.searchParams.set('ops_branch_id', contextWithoutReturn.branchId);
  target.searchParams.set('ops_stage', contextWithoutReturn.stage);
  target.searchParams.set('ops_ledger_view', contextWithoutReturn.ledgerView);
  target.searchParams.set('ops_stand', standId);
  target.searchParams.set('ops_return_anchor', returnAnchor);
  target.searchParams.set('ops_context_ref', contextRef);
  target.searchParams.set('return_to', returnTo);
  return canonicalInternalPath(target);
}

/** Parse the handoff atomically; partial, duplicate, or inconsistent context is rejected. */
export function parseOperationsPlanDraftHandoff(
  search: string,
  expected: OperationsPlanDraftHandoffExpectation = {},
): OperationsPlanDraftHandoffContext | null {
  const params = new URLSearchParams(search);
  if ([...params.keys()].some((key) => key.startsWith('ops_') && !CONTEXT_KEYS.has(key))) {
    return null;
  }
  const propertyId = uniquePart(params, 'ops_property');
  const standId = uniquePart(params, 'ops_stand');
  const returnAnchor = uniquePart(params, 'ops_return_anchor');
  const returnTo = uniqueReturnPath(params);
  const context = {
    originRoute: uniquePart(params, 'ops_origin'),
    propertyId,
    planId: uniquePart(params, 'ops_plan_id'),
    planRevision: uniquePart(params, 'ops_plan_revision'),
    draftRevision: uniquePart(params, 'ops_draft_revision'),
    revaluationRevision: uniquePart(params, 'ops_revaluation_revision'),
    runId: uniquePart(params, 'ops_run_id'),
    branchId: uniquePart(params, 'ops_branch_id'),
    stage: uniquePart(params, 'ops_stage'),
    ledgerView: uniquePart(params, 'ops_ledger_view'),
    standId,
    returnAnchor,
    returnTo,
    contextRef: uniquePart(params, 'ops_context_ref'),
  };
  if (!Object.values(context).every((value) => value !== null)) return null;
  const parsed = context as OperationsPlanDraftHandoffContext;
  if (
    parsed.originRoute !== operationsPlanPath(parsed.propertyId) ||
    parsed.stage !== OPERATIONS_PLAN_DRAFT_STAGE ||
    parsed.ledgerView !== OPERATIONS_PLAN_DRAFT_LEDGER_VIEW ||
    parsed.returnAnchor !== operationsPlanStandRowAnchor(parsed.standId) ||
    validReturnTo(
      parsed.returnTo,
      parsed.propertyId,
      parsed.planId,
      parsed.standId,
      parsed.returnAnchor,
    ) === null ||
    parsed.contextRef !== contextReference(parsed)
  ) {
    return null;
  }
  if (
    (expected.propertyId !== undefined && parsed.propertyId !== expected.propertyId) ||
    (expected.standId !== undefined && parsed.standId !== expected.standId)
  ) {
    return null;
  }
  return parsed;
}
